/**
 * Admin edit form.
 * Loads the admin, hydrates the form, then PATCHes profile / roles / scope in one submit.
 */
import * as React from 'react';
import {
  NotepadText,
  Building2,
  UserCog,
  User,
  Mail,
  Target,
  MapPin,
  BookOpen,
} from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import { Select } from '@/components/forms/Select';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { getAdmin, updateAdmin } from '@/services/admins.service';
import { fetchRoles } from '@/services/roles.service';
import { useEstablishmentsMultiselect } from '@/hooks/useEstablishmentsMultiselect';
import { useEnrichEstablishmentLabels } from '@/hooks/useEnrichEstablishmentLabels';
import { useAuthStore } from '@/stores/authStore';
import type { UpdateAdminDto } from '@/types/admin.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { adminEditValidationSchema } from '@/utils/validation-schemas-utils';
import { useContactExistsCheck, resolveContactCheckType } from '@/hooks/useContactExistsCheck';
import { ESTABLISHMENT_SEARCH_HINT } from '@/utils/format-establishment-select-label-utils';
import { ADMIN_FORM_QUERY_KEYS, STAFF_STATUS_FORM_OPTIONS } from './admin-configs';
import {
  adminEditValidationPayload,
  adminToEditFormData,
  applyAdminFormField,
  applyAdminPrimaryContactChange,
  buildUpdateAdminDto,
  deriveAdminFormFlags,
  fallbackHasInPersonFromAdmin,
  fallbackHasRemoteFromAdmin,
  getSelectedRolesData,
  mapDepartmentCodeOptions,
  mapDepartmentIdOptions,
  mapProgramSelectOptions,
  mapScopedEstablishmentOptions,
  resolveEditEstablishmentSourceUserId,
  resolveEditSelectedRoleCodes,
  rolesRequireResidenceDepartments,
  withCurrentManagerOption,
} from './admin-form-utils';
import {
  commitSplitContactSwap,
  canSwapPrimaryContact,
  areContactsIdentical,
  contactExistsErrorMessage,
  isPrimaryContactFormValid,
  isPrimaryContactSubmitBlocked,
  primaryContactFieldError,
  resolveStoredContact,
  isPrimaryContactDirty,
  phoneToE164,
  readPhoneSetupLink,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { ContactChangeModal } from '@/components/common/modals/ContactChangeModal';
import { renderCrossModeAfterSaveModal } from '@/components/common/modals/CrossModeAfterSaveModal';
import { useCrossModeAfterSave } from '@/hooks/useCrossModeAfterSave';
import { useContactChangeOnSubmit } from '@/hooks/useContactChangeOnSubmit';
import { Button } from '@/components/forms/Button';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { buildCreatableRoleOptions, getEditableRoles } from '@/utils/interface-hierarchy-utils';
import { useDepartments } from '@/hooks/useDepartments';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';

/** Admin id to load, plus close / success callbacks. */
interface EditAdminViewProps {
  adminId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Field-level validation accessors passed into the secondary-contact input. */
type AdminFieldValidation = {
  getFieldState: (field: string) => 'default' | 'error' | 'success' | undefined;
  getFieldError: (field: string) => string | undefined;
};

/** Props for {@link AdminSecondaryContactField}. */
type AdminSecondaryContactFieldProps = Readonly<{
  primaryContactType: 'email' | 'phone';
  email: string;
  phone: string;
  phoneCountryCode: string;
  contactExistsError?: string;
  validation: AdminFieldValidation;
  onFieldChange: (field: keyof UpdateAdminDto, value: string) => void;
  onBlur: (field: string) => void;
  onCountryChange: (code: string) => void;
}>;

/** Secondary email or phone input, opposite of the primary contact. */
function AdminSecondaryContactField({
  primaryContactType,
  email,
  phone,
  phoneCountryCode,
  contactExistsError,
  validation,
  onFieldChange,
  onBlur,
  onCountryChange,
}: AdminSecondaryContactFieldProps): React.ReactNode {
  /** Persist secondary phone digits. */
  function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('phone', e.target.value);
  }
  /** Persist secondary email. */
  function handleEmailChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('email', e.target.value);
  }
  /** Mark secondary phone touched. */
  function handlePhoneBlur(): void {
    onBlur('phone');
  }
  /** Mark secondary email touched. */
  function handleEmailBlur(): void {
    onBlur('email');
  }
  /** Persist the secondary-phone country ISO. */
  function handleCountryChange(country: { code: string }): void {
    onCountryChange(country.code);
  }
  if (primaryContactType === 'email') {
    return (
      <PhoneInput
        label="Numéro de téléphone (contact secondaire, optionnel)"
        value={phone}
        onChange={handlePhoneChange}
        onBlur={handlePhoneBlur}
        onCountryChange={handleCountryChange}
        countryCode={phoneCountryCode}
        state={contactExistsError ? 'error' : validation.getFieldState('phone')}
        error={contactExistsError ?? validation.getFieldError('phone')}
        helperText="Numéro de contact secondaire"
      />
    );
  }
  return (
    <Input
      type="text"
      label="Email (contact secondaire, optionnel)"
      value={email}
      onChange={handleEmailChange}
      onBlur={handleEmailBlur}
      icon={Mail}
      placeholder="Ex: contact@example.com"
      state={contactExistsError ? 'error' : validation.getFieldState('email')}
      error={contactExistsError ?? validation.getFieldError('email')}
      helperText="Email secondaire (le téléphone reste l'identifiant de connexion)"
    />
  );
}

/** Admin edit form. */
export const EditAdminView: React.FC<EditAdminViewProps> = ({ adminId, onClose, onSuccess }) => {
  const [formData, setFormData] = React.useState<UpdateAdminDto>({});
  const defaultCountry = useDefaultPhoneCountry();
  const [phoneCountryCode, setPhoneCountryCode] = React.useState<string>(defaultCountry);
  const [primaryContact, setPrimaryContact] = React.useState('');
  const [primaryContactType, setPrimaryContactType] = React.useState<'email' | 'phone'>('email');
  const [primaryContactCountryCode, setPrimaryContactCountryCode] = React.useState(defaultCountry);

  const validation = useFormValidation(adminEditValidationSchema);
  const { elements: adminElements } = useInterfaceElements('admins', { bothModes: true });
  const {
    data: admin,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ADMIN_FORM_QUERY_KEYS.admin(adminId),
    queryFn: () => getAdmin(adminId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'adminNotFound',
    onClose,
    entityId: adminId,
  });

  const secondaryContactType: 'email' | 'phone' =
    primaryContactType === 'email' ? 'phone' : 'email';
  const secondaryContactValue =
    primaryContactType === 'email' ? formData.phone || '' : formData.email || '';

  const { isChecking: isPrimaryChecking, exists: primaryContactExists } = useContactExistsCheck({
    value: primaryContact,
    type: resolveContactCheckType(primaryContactType),
    excludeUserId: adminId,
    skipHydratedValue: true,
  });

  const { isChecking: isSecondaryChecking, exists: contactExists } = useContactExistsCheck({
    value: secondaryContactValue,
    type: resolveContactCheckType(secondaryContactType),
    excludeUserId: adminId,
    skipHydratedValue: true,
  });

  const contactsAreIdentical = areContactsIdentical(primaryContact, secondaryContactValue);
  const primaryContactExistsError = primaryContactFieldError(
    primaryContactExists,
    primaryContactType,
    contactsAreIdentical,
  );
  const contactExistsError = contactExistsErrorMessage(contactExists, secondaryContactType);

  const { data: roles = [] } = useQuery({
    queryKey: ADMIN_FORM_QUERY_KEYS.roles,
    queryFn: fetchRoles,
  });
  const { targetMode, finishSave, handleStay, handleSwitch } = useCrossModeAfterSave({
    roles,
    listPath: '/admins',
    onSuccess,
    onClose,
  });

  const selectedRoleCodes = React.useMemo(
    () => resolveEditSelectedRoleCodes(formData.roleIds, roles, admin),
    [admin, formData.roleIds, roles],
  );
  const selectedRolesData = React.useMemo(
    () => getSelectedRolesData(formData.roleIds ?? [], roles),
    [formData.roleIds, roles],
  );
  const {
    hasInPersonMode,
    isManagerCategory,
    isCoordinatorCategory,
    isCoordinatorRole,
    isCoordinatorRemoteRole,
    requiresResidenceDepartments,
    showDepartmentField,
    showProgramField,
  } = deriveAdminFormFlags({
    selectedRoleCodes,
    selectedRolesData,
    showDepartmentForRecruiter: true,
    fallbackHasInPerson: fallbackHasInPersonFromAdmin(admin),
    fallbackHasRemote: fallbackHasRemoteFromAdmin(admin),
  });

  // Remote mode: departments and programs
  const { data: departments = [], error: departmentsError } = useDepartments('FR');

  const departmentOptions = React.useMemo(() => mapDepartmentIdOptions(departments), [departments]);

  const { user: currentUser, roles: currentUserRoles } = useAuthStore();
  const currentUserRoleCode = currentUserRoles?.[0]?.code;
  const currentUserId = currentUser?.id;

  const isPrimaryDirty = isPrimaryContactDirty({
    mode: primaryContactType,
    currentValue: primaryContact,
    storedEmail: admin?.email,
    storedPhone: admin?.phone,
    serverKind: admin?.primaryContactKind,
    defaultCountry,
  });

  /** Close the panel after a non-self contact change (or a clean PATCH). */
  function handleContactChangeFinished(): void {
    finishSave(formData.roleIds ?? []);
  }

  const contactChange = useContactChangeOnSubmit({
    isDirty: isPrimaryDirty,
    kind: primaryContactType,
    ownProfile: adminId === currentUserId,
    closeForm: handleContactChangeFinished,
    fallbackPhone:
      primaryContactType === 'phone'
        ? phoneToE164(primaryContact, primaryContactCountryCode || defaultCountry)
        : undefined,
  });

  // Autocomplete managers when editing an in-person coordinator.
  const {
    options: managersOptions,
    isLoading: managersLoading,
    onSearch: onManagersSearch,
  } = useAdminAutocomplete({
    roleCodes: ['manager'],
    enabled: isCoordinatorRole,
  });

  // Autocomplete remote managers when editing a remote coordinator.
  const {
    options: managersRemoteRawOptions,
    isLoading: managersRemoteLoading,
    onSearch: onManagersRemoteSearch,
  } = useAdminAutocomplete({
    roleCodes: ['manager_remote'],
    enabled: isCoordinatorRemoteRole,
  });

  // Fetch the current manager so the label shows without the user typing.
  const currentManagedById = formData.managedById;
  const { data: currentManagerData } = useQuery({
    queryKey: ADMIN_FORM_QUERY_KEYS.currentManager(currentManagedById!),
    queryFn: () => getAdmin(currentManagedById!),
    enabled: isCoordinatorRemoteRole && !!currentManagedById,
  });

  // Inject the current manager into options if the search results omit them.
  const managersRemoteOptions = React.useMemo(
    () =>
      withCurrentManagerOption(managersRemoteRawOptions, currentManagedById, currentManagerData),
    [managersRemoteRawOptions, currentManagedById, currentManagerData],
  );

  // Load establishments for the selected manager.
  const establishmentSourceUserId = React.useMemo(
    () =>
      resolveEditEstablishmentSourceUserId({
        isCoordinatorRole,
        currentUserRoleCode,
        currentUserId,
      }),
    [currentUserRoleCode, currentUserId, isCoordinatorRole],
  );

  const { data: scopedEstablishmentsResponse } = useQuery({
    queryKey: ['users', 'establishments', establishmentSourceUserId],
    queryFn: async () => {
      const id = establishmentSourceUserId;
      if (!id) {
        return { data: [] };
      }
      const user = await getAdmin(id);
      return {
        data: user.userEstablishments?.map((ue) => ue.establishment) || [],
      };
    },
    enabled:
      !!hasInPersonMode &&
      !!(isManagerCategory || isCoordinatorCategory) &&
      !!establishmentSourceUserId,
  });
  const scopedEstablishments = scopedEstablishmentsResponse?.data || [];

  const {
    isLoading: estabSearchLoading,
    onSearch: onEstabSearch,
    onLoadMore: onEstabLoadMore,
    hasMore: estabHasMore,
    getOptionsForValues: getEstabOptions,
    primeLabelCache: primeEstabLabelCache,
  } = useEstablishmentsMultiselect();

  useEnrichEstablishmentLabels(formData.establishmentIds || [], primeEstabLabelCache);

  const { data: programsResponse } = useProgramsForMode({
    mode: 'remote',
    pageSize: 100,
    enabled: showProgramField,
  });
  const programs = programsResponse?.data || [];

  /** Hydrate from GET admin and prime establishment labels so {@link MultiSelect} shows names. */
  React.useEffect(() => {
    if (!admin) return;
    const {
      formData: initialData,
      phoneCountryCode: nextPhoneCountry,
      labelEntries,
    } = adminToEditFormData(admin, defaultCountry);
    primeEstabLabelCache(labelEntries);
    setFormData(initialData);
    setPhoneCountryCode(nextPhoneCountry);
    const stored = resolveStoredContact(
      admin.email ?? null,
      admin.phone ?? null,
      admin.primaryContactKind,
    );
    setPrimaryContact(stored.displayValue);
    setPrimaryContactType(stored.mode);
    setPrimaryContactCountryCode(stored.isoCode);
    validation.validateInitialFormData(
      adminEditValidationPayload({
        form: initialData,
        primaryContact: stored.displayValue,
        primaryContactType: stored.mode,
        primaryContactCountryCode: stored.isoCode,
        primaryContactExists: null,
        requiresResidenceDepartments: rolesRequireResidenceDepartments(
          roles,
          initialData.roleIds ?? [],
        ),
        contactExists: null,
        phoneCountryCode: nextPhoneCountry,
      }),
    );
  }, [admin, primeEstabLabelCache, defaultCountry, roles]);

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateAdminDto) => updateAdmin(adminId, dto),
    onSuccess: (data) => {
      if (contactChange.isIntercepting) {
        contactChange.onMutationSettled({ ok: true, link: readPhoneSetupLink(data) });
        return;
      }
      toast.success('Administrateur modifié avec succès !');
      finishSave(formData.roleIds ?? []);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
      contactChange.onMutationSettled({ ok: false });
    },
  });

  /** Set one field and re-validate (phone / residence-department context). */
  const handleFieldChange = (field: keyof UpdateAdminDto, value: any) => {
    const updatedData = applyAdminFormField(formData, field, value);
    setFormData(updatedData);

    const isSecondaryContact = field === 'phone' || field === 'email';

    if (field === 'phone') {
      validation.validateField(field as string, value, {
        ...updatedData,
        _phoneCountryCode: phoneCountryCode,
        _contactExists: isSecondaryContact ? contactExists : undefined,
      });
    } else {
      validation.validateField(field as string, value, {
        ...updatedData,
        _requiresResidenceDepartments: rolesRequireResidenceDepartments(
          roles,
          updatedData.roleIds ?? [],
        ),
        _contactExists: isSecondaryContact ? contactExists : undefined,
      });
    }

    validation.markAsTouched(field as string);
    validation.markAsDirty(field as string);
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Swap primary and secondary, then re-validate the contact field. */
  function handleSwapContacts(): void {
    setFormData(
      commitSplitContactSwap({
        primaryContact,
        primaryContactType,
        primaryCountryCode: primaryContactCountryCode,
        formData,
        secondaryCountryCode: phoneCountryCode,
        setPrimaryContact,
        setPrimaryContactType,
        setPrimaryContactCountryCode,
        setSecondaryCountryCode: setPhoneCountryCode,
        validation,
      }),
    );
  }

  /** Patch primary contact, keep form email/phone in sync, re-validate. */
  function handlePrimaryContactChange(
    value: string,
    type: 'email' | 'phone',
    countryCode?: string,
  ): void {
    setPrimaryContact(value);
    setPrimaryContactType(type);
    setPrimaryContactCountryCode(countryCode ?? primaryContactCountryCode);
    const updated = applyAdminPrimaryContactChange(formData, value, type);
    setFormData(updated);
    validation.validateField('contact', value, {
      ...updated,
      _contactType: type,
      contactCountryCode: countryCode ?? primaryContactCountryCode,
      _phoneCountryCode:
        type === 'email' ? phoneCountryCode : (countryCode ?? primaryContactCountryCode),
      _primaryContactExists: primaryContactExists,
    });
    validation.markAsTouched('contact');
    validation.markAsDirty('contact');
  }

  /** Validate then PATCH; DTO fields depend on coordinator vs manager vs remote. */
  function handleSubmit(): void {
    if (
      isPrimaryContactSubmitBlocked(primaryContactExists, isPrimaryChecking, contactsAreIdentical)
    )
      return;
    const isValid = validation.validateForm(
      adminEditValidationPayload({
        form: formData,
        primaryContact,
        primaryContactType,
        primaryContactCountryCode,
        primaryContactExists,
        requiresResidenceDepartments,
        contactExists,
        phoneCountryCode,
      }),
    );

    if (!isValid) {
      toast.error(getErrorMessage('requiredFields'));
      return;
    }

    contactChange.requestSubmit(() => {
      updateMutation.mutate(
        buildUpdateAdminDto({
          form: formData,
          primaryContact,
          primaryContactType,
          primaryContactCountryCode,
          secondaryContact: secondaryContactValue,
          secondaryContactCountryCode: phoneCountryCode,
          defaultCountry,
          requiresResidenceDepartments,
          isCoordinatorRole,
          showProgramField,
          currentUserRoleCode,
        }),
      );
    });
  }

  const adminRoleOptions = React.useMemo(
    () => buildCreatableRoleOptions(getEditableRoles(adminElements), roles),
    [adminElements, roles],
  );
  const isSingleRoleMode = adminRoleOptions.length <= 1;

  return (
    <>
      <SlidePanelForm
        formType="edit"
        title="Modifier le collaborateur"
        subtitle="Modifiez les informations du collaborateur"
        onClose={onClose}
        onSubmit={handleSubmit}
        submitLabel="Enregistrer"
        isSubmitting={updateMutation.isPending || contactChange.modal.open}
        isValid={isPrimaryContactFormValid({
          primaryExists: primaryContactExists,
          isPrimaryChecking,
          isSecondaryChecking,
          identical: contactsAreIdentical,
          formValid: validation.isFormValid(),
        })}
        isLoading={isLoading || (!!admin && Object.keys(formData).length === 0)}
      >
        {!isSingleRoleMode && (
          <div className="form-section">
            <h4 className="form-section-title">
              <NotepadText className="w-4 h-4" />
              Rôle(s)
            </h4>
            <MultiSelect
              label="Rôles"
              placeholder="Sélectionner les rôles"
              options={adminRoleOptions}
              value={formData.roleIds || []}
              onChange={(values) => handleFieldChange('roleIds', values)}
              onBlur={() => handleBlur('roleIds')}
              maxVisibleTags={3}
              required
              helperText="Sélectionnez les rôles administratifs (Admin, Coordinateur, Manager)"
              state={validation.getFieldState('roleIds')}
              error={validation.getFieldError('roleIds')}
            />
            <p className="text-xs text-muted-foreground mt-2">
              Les rôles et profils associés seront automatiquement mis à jour
            </p>
          </div>
        )}

        <div className="form-section">
          <h4 className="form-section-title">
            <User className="w-4 h-4" />
            Informations personnelles
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Prénom"
              value={formData.firstName || ''}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              onBlur={() => handleBlur('firstName')}
              placeholder="Ex: Marie"
              maxLength={100}
              state={validation.getFieldState('firstName')}
              error={validation.getFieldError('firstName')}
            />
            <Input
              label="Nom"
              value={formData.lastName || ''}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              onBlur={() => handleBlur('lastName')}
              placeholder="Ex: Martin"
              maxLength={100}
              state={validation.getFieldState('lastName')}
              error={validation.getFieldError('lastName')}
            />
            <Select
              label="Statut"
              value={formData.status || 'active'}
              onChange={(value) => handleFieldChange('status', value)}
              onBlur={() => handleBlur('status')}
              options={[...STAFF_STATUS_FORM_OPTIONS]}
              state={validation.getFieldState('status')}
              error={validation.getFieldError('status')}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <SmartContactInput
              label="Contact principal (identifiant)"
              value={primaryContact}
              onContactChange={handlePrimaryContactChange}
              storedContact={{
                primaryKind: primaryContactType,
                serverPrimaryKind: admin?.primaryContactKind,
                email: admin?.email,
                phone: admin?.phone,
              }}
              error={primaryContactExistsError ?? validation.getFieldError('contact')}
              state={primaryContactExistsError ? 'error' : validation.getFieldState('contact')}
              showContactActions
              emailVerified={admin?.emailVerified}
              userId={adminId}
              swapButton={
                canSwapPrimaryContact(primaryContactType) ? (
                  <Button
                    type="button"
                    preset="contact-swap"
                    size="md"
                    onClick={handleSwapContacts}
                  />
                ) : undefined
              }
            />
            <AdminSecondaryContactField
              primaryContactType={primaryContactType}
              email={formData.email || ''}
              phone={formData.phone || ''}
              phoneCountryCode={phoneCountryCode}
              contactExistsError={contactExistsError}
              validation={validation}
              onFieldChange={handleFieldChange}
              onBlur={handleBlur}
              onCountryChange={setPhoneCountryCode}
            />
          </div>
        </div>

        {isCoordinatorRole && (
          <div className="form-section">
            <h4 className="form-section-title">
              <UserCog className="w-4 h-4" />
              Manager superviseur
            </h4>
            <Autocomplete
              label="Manager"
              placeholder="Sélectionner un manager"
              options={managersOptions}
              value={formData.managedById || ''}
              onChange={(value) => handleFieldChange('managedById', value)}
              onSearch={onManagersSearch}
              isLoading={managersLoading}
              minSearchLength={2}
              onBlur={() => handleBlur('managedById')}
            />
          </div>
        )}

        {requiresResidenceDepartments && (
          <div className="form-section">
            <h4 className="form-section-title">
              <MapPin className="w-4 h-4" />
              Département
            </h4>
            <MultiSelect
              label="Département(s)"
              placeholder="Sélectionner un ou plusieurs départements"
              options={departmentOptions}
              value={formData.residenceDepartmentIds || []}
              onChange={(values) => handleFieldChange('residenceDepartmentIds', values)}
              onBlur={() => handleBlur('residenceDepartmentIds')}
              maxVisibleTags={3}
              required
              state={validation.getFieldState('residenceDepartmentIds')}
              error={
                departmentsError
                  ? getErrorMessage(departmentsError)
                  : validation.getFieldError('residenceDepartmentIds')
              }
            />
          </div>
        )}

        {isCoordinatorRemoteRole && (
          <div className="form-section">
            <h4 className="form-section-title">
              <UserCog className="w-4 h-4" />
              Géré par
            </h4>
            <Autocomplete
              label="Géré par"
              required
              placeholder="Rechercher un gestionnaire..."
              options={managersRemoteOptions}
              value={formData.managedById || ''}
              onChange={(value) => handleFieldChange('managedById', value || '')}
              onSearch={onManagersRemoteSearch}
              isLoading={managersRemoteLoading}
              minSearchLength={2}
              helperText="Le gestionnaire qui supervisera ce coordinateur"
            />
          </div>
        )}

        {isCoordinatorRole && currentUserRoleCode === 'admin' && (
          <div className="form-section">
            <h4 className="form-section-title">
              <Target className="w-4 h-4" />
              Objectif de prospection
            </h4>
            <Input
              label="Objectif annuel d'établissements à prospecter"
              type="number"
              min={0}
              value={formData.annualProspectionGoal ?? ''}
              onChange={(e) => handleFieldChange('annualProspectionGoal', e.target.value)}
              onBlur={() => handleBlur('annualProspectionGoal')}
              placeholder="Ex: 20"
              helperText="Nombre d'établissements à prospecter sur l'année (facultatif)"
            />
          </div>
        )}

        {hasInPersonMode && (isManagerCategory || isCoordinatorCategory) && (
          <div className="form-section">
            <h4 className="form-section-title">
              <Building2 className="w-4 h-4" />
              Établissements gérés
            </h4>
            {establishmentSourceUserId ? (
              <MultiSelect
                label="Établissements"
                placeholder="Sélectionner les établissements"
                options={mapScopedEstablishmentOptions(scopedEstablishments)}
                value={formData.establishmentIds || []}
                onChange={(values) => handleFieldChange('establishmentIds', values)}
                onBlur={() => handleBlur('establishmentIds')}
                maxVisibleTags={2}
                helperText="Établissements dont le manager a la gestion (optionnel)"
                state={validation.getFieldState('establishmentIds')}
                error={validation.getFieldError('establishmentIds')}
                placement="top"
              />
            ) : (
              <MultiSelect
                label="Établissements"
                placeholder="Rechercher un établissement..."
                options={getEstabOptions(formData.establishmentIds || [])}
                value={formData.establishmentIds || []}
                onChange={(values) => handleFieldChange('establishmentIds', values)}
                onBlur={() => handleBlur('establishmentIds')}
                onSearch={onEstabSearch}
                onLoadMore={onEstabLoadMore}
                hasMore={estabHasMore}
                isSearchLoading={estabSearchLoading}
                searchPlaceholder="Rechercher un établissement..."
                minSearchLength={2}
                maxVisibleTags={2}
                showSelectAll={false}
                helperText={ESTABLISHMENT_SEARCH_HINT}
                state={validation.getFieldState('establishmentIds')}
                error={validation.getFieldError('establishmentIds')}
                placement="top"
              />
            )}
          </div>
        )}

        {showDepartmentField && (
          <div className="form-section">
            <h4 className="form-section-title">
              <MapPin className="w-4 h-4" />
              Département(s)
            </h4>
            <MultiSelect
              label="Département(s)"
              placeholder="Sélectionner les départements"
              options={mapDepartmentCodeOptions(departments)}
              value={formData.departments || []}
              onChange={(values) => handleFieldChange('departments', values)}
              maxVisibleTags={3}
              helperText="Département(s) de rattachement"
              required
            />
          </div>
        )}

        {showProgramField && (
          <div className="form-section">
            <h4 className="form-section-title">
              <BookOpen className="w-4 h-4" />
              Programme(s)
            </h4>
            <MultiSelect
              label="Programme(s)"
              placeholder="Sélectionner les programmes"
              options={mapProgramSelectOptions(programs)}
              value={formData.programIds || []}
              onChange={(values) => handleFieldChange('programIds', values)}
              maxVisibleTags={3}
              helperText="Programme(s) gérés par ce coordinateur"
              required
              placement="top"
            />
          </div>
        )}
      </SlidePanelForm>
      {renderCrossModeAfterSaveModal({
        targetMode,
        listLabel: 'Administration',
        onStay: handleStay,
        onSwitch: handleSwitch,
      })}
      <ContactChangeModal
        open={contactChange.modal.open}
        kind={contactChange.modal.kind}
        ownProfile={contactChange.modal.ownProfile}
        phase={contactChange.modal.phase}
        phoneSetupLink={contactChange.modal.phoneSetupLink}
        onClose={contactChange.cancel}
        onConfirm={contactChange.confirm}
        onCopyLink={contactChange.onCopyLink}
      />
    </>
  );
};
